import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import { BsSearch, BsArrowRight, BsLightning, BsX, BsChatDots } from 'react-icons/bs';
import './AIHero.css';

interface AIHeroProps {
  tenant?: any;
  branding?: any;
  isWhiteLabel?: boolean;
}

const AIHero: React.FC<AIHeroProps> = ({ tenant, branding, isWhiteLabel }) => {
  const [query, setQuery] = useState('');
  const [selectedCriteria, setSelectedCriteria] = useState<string[]>([]);
  const [typingText, setTypingText] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();
  
  // Dynamic prompts for typing effect
  const prompts = [
    "Type a message...",
    "Show me homes for sale in East Valley",
    "What's my home worth in Gilbert?",
    "Find me luxury homes near Camelback Mountain",
    "I'm thinking about selling my home",
    "I need family homes in Gilbert",
    "How much are homes selling for in Chandler?",
    "Show me luxury estates in Paradise Valley",
    "Find me affordable homes in Tempe"
  ];
  
  // Typing effect
  useEffect(() => {
    const currentPrompt = prompts[promptIndex];
    let charIndex = 0;
    let isDeleting = false;
    let typingSpeed = 100;
    
    const typeEffect = setInterval(() => {
      if (!isDeleting) {
        setTypingText(currentPrompt.slice(0, charIndex));
        charIndex++;
        
        if (charIndex > currentPrompt.length) {
          isDeleting = true;
          typingSpeed = 2000; // Pause at full text
        }
      } else {
        setTypingText(currentPrompt.slice(0, charIndex));
        charIndex--;
        typingSpeed = 50;
        
        if (charIndex === 0) {
          setPromptIndex((prev) => (prev + 1) % prompts.length);
          isDeleting = false;
          typingSpeed = 500; // Pause before next prompt
        }
      }
    }, typingSpeed);
    
    return () => clearInterval(typeEffect);
  }, [promptIndex]);
  
  // Neural network animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Neural network nodes
    interface Node {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      pulsePhase: number;
      connections: number[];
    }
    
    const nodes: Node[] = [];
    const nodeCount = 80;
    
    // Initialize nodes
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        pulsePhase: Math.random() * Math.PI * 2,
        connections: []
      });
    }
    
    // Animation loop
    const animate = () => {
      ctx.fillStyle = 'rgba(15, 23, 42, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw nodes
      nodes.forEach((node, i) => {
        // Update position
        node.x += node.vx;
        node.y += node.vy;
        node.pulsePhase += 0.02;
        
        // Bounce off walls
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        
        // Find nearby nodes for connections
        node.connections = [];
        nodes.forEach((otherNode, j) => {
          if (i !== j) {
            const dx = node.x - otherNode.x;
            const dy = node.y - otherNode.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < 150) {
              node.connections.push(j);
              
              // Draw connection
              const opacity = 1 - (distance / 150);
              ctx.strokeStyle = `rgba(59, 130, 246, ${opacity * 0.3})`;
              ctx.beginPath();
              ctx.moveTo(node.x, node.y);
              ctx.lineTo(otherNode.x, otherNode.y);
              ctx.stroke();
            }
          }
        });
        
        // Draw node
        const pulseFactor = 1 + Math.sin(node.pulsePhase) * 0.2;
        ctx.fillStyle = node.connections.length > 3 ? '#3b82f6' : '#64748b';
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * pulseFactor, 0, Math.PI * 2);
        ctx.fill();
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  // Search criteria
  const searchOptions = {
    features: ["pool", "smart home", "solar panels", "EV charging"],
    locations: ["Scottsdale", "Phoenix", "Tempe", "Gilbert", "Chandler"],
    styles: ["modern", "contemporary", "traditional", "ranch"],
    priorities: ["energy efficient", "walkable", "quiet neighborhood", "investment potential"]
  };
  
  const toggleCriteria = (criteria: string) => {
    setSelectedCriteria(prev => {
      if (prev.includes(criteria)) {
        return prev.filter(c => c !== criteria);
      } else {
        return [...prev, criteria];
      }
    });
  };
  
  const buildSearchQuery = () => {
    if (selectedCriteria.length === 0) return query;
    return selectedCriteria.join(' ') + ' ' + query;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalQuery = buildSearchQuery();
    
    if (finalQuery.trim() || selectedCriteria.length > 0) {
      // Navigate to the search page with AI mode and the search query
      navigate('/search', { state: { initialQuery: finalQuery } });
    }
  };
  
  const clearAllCriteria = () => {
    setSelectedCriteria([]);
    setQuery('');
  };
  
  return (
    <div className="ai-hero">
      <canvas ref={canvasRef} className="neural-canvas"></canvas>
      
      <div className="ai-hero-content">
        <div className="ai-hero-chat-icon">
          <BsChatDots />
        </div>
        <div className="ai-badge">
          <BsLightning className="ai-icon" />
          AI-Powered Search
        </div>
        
        <h1 className="ai-hero-title">
          {isWhiteLabel && tenant ? 
            `Find Your Perfect Home with ${tenant.companyName}` : 
            'Find Your Perfect Home with AI'}
        </h1>
        
        <p className="ai-hero-subtitle">
          {isWhiteLabel && tenant?.tagline ? 
            tenant.tagline : 
            'Just describe what you want. Our AI understands.'}
        </p>
        
        <div className="ai-free-mls-badge">
          <span>🏠 Free MLS Home Search</span>
        </div>
        
        <Form onSubmit={handleSubmit} className="ai-search-form">
          <div className="ai-search-container">
            <BsChatDots className="ai-search-icon" />
            <input
              type="text"
              placeholder={typingText}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="ai-search-input"
              autoFocus
            />
            <Button 
              type="submit" 
              className="ai-search-button"
              disabled={!query.trim() && selectedCriteria.length === 0}
            >
              Send
              <BsArrowRight className="ms-2" />
            </Button>
          </div>
        </Form>
        
        {selectedCriteria.length > 0 && (
          <div className="ai-selected-criteria">
            {selectedCriteria.map((criteria) => (
              <span key={criteria} className="ai-selected-chip">
                {criteria}
                <BsX className="ai-remove-chip" onClick={() => toggleCriteria(criteria)} />
              </span>
            ))}
            <button className="ai-clear-all" onClick={clearAllCriteria}>
              Clear all
            </button>
          </div>
        )}
        
        {/* Quick filters commented out for cleaner hero
        <div className="ai-quick-filters">
          <div className="filter-section">
            <span className="filter-label">Popular:</span>
            {searchOptions.features.map((feature) => (
              <button
                key={feature}
                onClick={() => toggleCriteria(feature)}
                className={`ai-filter-chip ${selectedCriteria.includes(feature) ? 'active' : ''}`}
              >
                {feature}
              </button>
            ))}
          </div>
          
          <div className="filter-section">
            <span className="filter-label">Location:</span>
            {searchOptions.locations.map((location) => (
              <button
                key={location}
                onClick={() => toggleCriteria(location)}
                className={`ai-filter-chip ${selectedCriteria.includes(location) ? 'active' : ''}`}
              >
                {location}
              </button>
            ))}
          </div>
        </div>
        */}
        
        <div className="ai-trust-indicators">
          <span className="trust-item">🔒 Privacy First</span>
          <span className="trust-item">⚡ Instant Results</span>
          <span className="trust-item">🎯 98% Match Accuracy</span>
        </div>
      </div>
    </div>
  );
};

export default AIHero;